import { useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { streamChat, generateFromChat } from '@/lib/ai-pipeline-api';
import { normalizeGeneratedPipeline, type NormalizedGeneratedPipeline } from '@/lib/ai-pipeline-normalizer';
import type {
  ChatMessage,
  PipelineOption,
} from '@/types/ai-pipeline';

function generateId(): string {
  return crypto.randomUUID?.() || Math.random().toString(36).slice(2, 11);
}

interface UseAIChatOptions {
  dataSourceIds: string[];
  onPipelineGenerated?: (pipeline: NormalizedGeneratedPipeline) => void;
}

export function useAIChat({ dataSourceIds, onPipelineGenerated }: UseAIChatOptions) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [suggestedPipelines, setSuggestedPipelines] = useState<PipelineOption[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const sendMessage = useCallback(
    (text: string) => {
      if (!text.trim() || isStreaming || dataSourceIds.length === 0) return;

      const userMessage: ChatMessage = {
        id: generateId(),
        role: 'user',
        content: text.trim(),
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, userMessage]);

      const assistantId = generateId();
      const assistantMessage: ChatMessage = {
        id: assistantId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMessage]);
      setIsStreaming(true);

      const controller = streamChat(
        {
          conversationId: conversationId || undefined,
          dataSourceIds,
          message: text.trim(),
        },
        {
          onToken: ({ content }) => {
            setMessages(prev =>
              prev.map(m =>
                m.id === assistantId
                  ? { ...m, content: m.content + content }
                  : m
              )
            );
          },
          onSuggestion: ({ pipelines }) => {
            setSuggestedPipelines(pipelines);
            setMessages(prev =>
              prev.map(m =>
                m.id === assistantId
                  ? { ...m, suggestions: pipelines }
                  : m
              )
            );
          },
          onDone: ({ conversationId: newId }) => {
            setConversationId(newId);
            setIsStreaming(false);
          },
          onError: ({ message }) => {
            toast.error(message || 'Chat error');
            setIsStreaming(false);
          },
        }
      );

      abortControllerRef.current = controller;
    },
    [conversationId, dataSourceIds, isStreaming]
  );

  const generatePipeline = useCallback(
    async (pipelineOptionId?: string) => {
      if (!conversationId) {
        toast.error('No active conversation');
        return;
      }

      setIsGenerating(true);

      try {
        const selectedOption =
          pipelineOptionId
            ? suggestedPipelines.find((option) => option.id === pipelineOptionId) || null
            : null;
        const result = await generateFromChat({
          conversationId,
          pipelineOptionId: pipelineOptionId || '',
        });
        const normalizedPipeline = normalizeGeneratedPipeline(result.data, dataSourceIds, {
          selectedOption,
          contextDataSources: result.meta?.context?.dataSources,
        });

        toast.success('Pipeline generated successfully!');
        onPipelineGenerated?.(normalizedPipeline);
      } catch (error: any) {
        toast.error(error?.message || 'Failed to generate pipeline');
      } finally {
        setIsGenerating(false);
      }
    },
    [conversationId, dataSourceIds, onPipelineGenerated, suggestedPipelines]
  );

  const stopStreaming = useCallback(() => {
    abortControllerRef.current?.abort();
    setIsStreaming(false);
  }, []);

  const resetChat = useCallback(() => {
    abortControllerRef.current?.abort();
    setMessages([]);
    setSuggestedPipelines([]);
    setConversationId(null);
    setIsStreaming(false);
    setIsGenerating(false);
  }, []);

  return {
    messages,
    sendMessage,
    isStreaming,
    isGenerating,
    suggestedPipelines,
    generatePipeline,
    conversationId,
    stopStreaming,
    resetChat,
  };
}
